import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocale } from '../../../context/LocaleContext';
import { ChatMessage, TypingIndicator } from '../../../entities/chat-message';
import { ClearChatDialog } from '../../../features/clear-chat-history';
import { useExportChatPdf } from '../../../features/export-chat-pdf';
import { Spinner } from '../../../shared/ui/spinner';
import { useChat } from '../model/use-chat';
import { ChatInput } from './chat-input';
import { ChatMenu } from './chat-menu';
import { QuickQuestions } from './quick-questions';

import cashGPTIcon from '../../../shared/assets/brand/cashgpt_icon_new.png';

import styles from './ai-advisor-page.module.css';

export const AIAdvisorPage = () => {
	const intl = useIntl();
	const { setErrorModalShow } = useLocale();
	const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
	const messagesEndRef = useRef(null);

	const showError = message => setErrorModalShow({ title: 'Error', message });
	const { messages, isLoadingHistory, isSending, sendMessage, resetChat } =
		useChat({ onError: showError });
	const { exportPdf, isExporting } = useExportChatPdf({ onError: showError });

	const hasUserMessages = messages.some(m => m.type === 'user');
	const isWelcomeOnly = messages.length === 1 && !hasUserMessages;

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [messages, isSending]);

	return (
		<div className={styles.container}>
			<div className={styles.content}>
				<div className={styles.chatHeader}>
					<div className={styles.titleSection}>
						<div className={styles.botIcon}>
							<img
								src={cashGPTIcon}
								alt='CashGPT'
								className={styles.botIconImage}
							/>
						</div>
						<div>
							<h1 className={styles.title}>
								{intl.formatMessage({ id: 'AIAdvisorID1' })}
							</h1>
							<p className={styles.subtitle}>
								{intl.formatMessage({ id: 'AIAdvisorID2' })}
							</p>
						</div>
					</div>
				</div>

				<div className={styles.chatContainer}>
					{isLoadingHistory ? (
						<div className={styles.loading}>
							<Spinner />
						</div>
					) : (
						<>
							<div className={styles.messages}>
								{messages.map(message => (
									<ChatMessage key={message.id} message={message} />
								))}
								{isSending && <TypingIndicator />}
								<div ref={messagesEndRef} />
							</div>
							{isWelcomeOnly && (
								<QuickQuestions onSelect={sendMessage} disabled={isSending} />
							)}
						</>
					)}
				</div>

				<ChatInput onSend={sendMessage} isBusy={isSending}>
					<ChatMenu
						onClear={() => setIsClearDialogOpen(true)}
						onExport={exportPdf}
						isExporting={isExporting}
						canExport={hasUserMessages}
						disabled={isSending}
					/>
				</ChatInput>
			</div>

			<ClearChatDialog
				isOpen={isClearDialogOpen}
				onClose={() => setIsClearDialogOpen(false)}
				onCleared={resetChat}
				onError={showError}
			/>
		</div>
	);
};
